import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon } from '@southfields-digital/mxmz-react-ui';
import { Fragment, useMemo } from 'react';

import { RemoveButton } from './buttons';
import Cell from './Cell';
import Repeater from './Repeater';
import styles from './Repeater.module.scss';
import { Id, Items } from './types';
import { isRepeater } from './utils';

type HorizontalRepeaterProps = {
  items: Items[];
  parentRowIndex?: number;
  handleChangeRepeater: (id: Id, rowIndex: number, rows: Items[]) => void;
  handleChangeValue: (id: Id, rowIndex: number, value: string) => void;
  handleClickAdd: () => void;
  handleClickRemove: (index: number) => void;
  buttonClassNames?: string;
  excludedIds?: Id[];
  isDisabled?: boolean;
  handleSelectMedia?: (
    properties: ImageProperties,
    handleSelectValue?: (value: string) => void
  ) => void;
};

type HorizontalRepeaterRowProps = Omit<
  HorizontalRepeaterProps,
  'items' | 'handleClickAdd' | 'hadleClickRemove'
> & {
  item: Items;
  handleClickRemove: () => void;
};

const HorizontalRepeaterRow = ({
  item,
  parentRowIndex = 0,
  handleClickRemove,
  handleChangeRepeater,
  handleChangeValue,
  buttonClassNames,
  excludedIds,
  isDisabled,
  handleSelectMedia,
}: HorizontalRepeaterRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: `HorizontalRepeater-${parentRowIndex}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const currentItems = useMemo(
    () => Object.values(item).filter(({ id }) => !excludedIds?.includes(id)),
    [item, excludedIds]
  );

  return (
    <tr key={parentRowIndex} ref={setNodeRef} style={style}>
      <td {...listeners} {...attributes} className="w-[30px] h-[30px] relative text-gray-500">
        <Icon icon="sort" weight="bold" />
      </td>
      <td>
        <table>
          <tbody className={styles.HorizontalRowContainer}>
            {currentItems.map((currentItem) => (
              <Fragment key={currentItem.id}>
                <tr>
                  <th>{currentItem.name || ''}</th>
                </tr>
                <tr>
                  <td>
                    {isRepeater(currentItem) ? (
                      <Repeater
                        parentRowIndex={parentRowIndex}
                        onChangeRows={handleChangeRepeater}
                        handleSelectMedia={handleSelectMedia}
                        {...currentItem}
                      />
                    ) : (
                      <Cell
                        item={currentItem}
                        rowIndex={parentRowIndex}
                        handleChangeValue={handleChangeValue}
                        handleSelectMedia={handleSelectMedia}
                      />
                    )}
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </td>
      {!isDisabled && (
        <td className={buttonClassNames}>
          <RemoveButton onClick={handleClickRemove} />
        </td>
      )}
    </tr>
  );
};

export default HorizontalRepeaterRow;
